"use client"
import { createPortal } from "react-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faEnvelope, faCalendarAlt } from "@fortawesome/free-solid-svg-icons"
import { useForm } from "react-hook-form"
import { useDispatch } from "react-redux"
import { useEffect, useState, useCallback } from "react"
import { addInterviewAsync } from "./Candidate/CandidateSlice/Interview/InterviewSlice"
import { updateJobStatusAsync } from "./Candidate/CandidateSlice/Basic/CandidateSlice"

const Modal = ({ candidate, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [mounted, setMounted] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      applicant_uuid: candidate?.uuid || '',
      status: 'interview_scheduled',
      duration: '',
      call_type: '',
      interview_date: '',
      interview_time: '',
      message: '',
      hiring_team_email: ''
    }
  })

  const dispatch = useDispatch()

  // Set mounted state to handle portal rendering
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])

  // Set form values when candidate changes
  useEffect(() => {
    if (candidate?.uuid) {
      setValue('applicant_uuid', candidate.uuid)
    }
  }, [candidate, setValue])

  // Enhanced form submission with better error handling
  const onSubmit = useCallback(async (formData) => {
    if (isSubmitting) return // Prevent double submission

    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // Validate candidate data
      if (!candidate?.uuid) {
        throw new Error('Candidate information is missing')
      }

      // Add interview
      const interviewResult = await dispatch(addInterviewAsync({
        newInterview: formData
      })).unwrap()

      // Update job status
      await dispatch(updateJobStatusAsync({
        uuid: candidate.uuid,
        newStatus: "interview_scheduled",
      })).unwrap()

      // Success handling
      reset()

      // Call success callback if provided, otherwise reload
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(interviewResult)
      } else {
        window.location.reload()
      }

      onClose()

    } catch (error) {
      console.error("Error submitting interview:", error)
      setSubmitError(error.message || "Failed to schedule interview. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }, [dispatch, candidate, reset, onClose, onSuccess, isSubmitting])

  // Enhanced close handler
  const handleClose = useCallback((e) => {
    if (isSubmitting) return // Prevent closing during submission

    // Reset form and clear errors when closing
    reset()
    setSubmitError(null)
    onClose()
  }, [onClose, reset, isSubmitting])

  // Keyboard event handler for ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isSubmitting) {
        handleClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleClose, isSubmitting])

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  // Don't render until mounted to avoid SSR issues
  if (!mounted) return null

  // Don't render if candidate data is missing
  if (!candidate) {
    console.error('Modal: Candidate data is required')
    return null
  }

  const modalContent = (
    <div
      className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleClose}
    >
      <div
        className="bg-white w-full sm:w-[67%] pt-3 rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between px-3 border-b w-full">
          <div className="inline-flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-lg pr-3 text-blue-600" />
            <h2 className="text-lg font-bold">
              Schedule Interview - {candidate?.candidate?.name || candidate?.name || 'Candidate'}
            </h2>
          </div>
          <button
            className="text-gray-500 hover:text-red-500 p-1 rounded"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close modal"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Error Display */}
        {submitError && (
          <div className="mx-3 mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {submitError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow-md mx-auto">
          <div className="flex flex-wrap border-b mb-3">
            {/* Left Column */}
            <div className="w-full md:w-1/2 px-3 pr-4 pt-6 bg-gray-100">
              {/* Duration Dropdown */}
              <div className="mb-4">
                <label className="block text-blue-700 font-bold mb-2" htmlFor="duration">
                  Duration <span className="text-red-500">*</span>
                </label>
                <select
                  {...register("duration", {
                    required: "Duration is required",
                  })}
                  className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.duration ? "border-red-500" : "border-gray-300"
                    }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select duration</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1.5 hours</option>
                  <option value="120">2 hours</option>
                </select>
                {errors.duration && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.duration.message}</span>
                )}
              </div>

              {/* Hidden inputs */}
              <input
                type="hidden"
                {...register("applicant_uuid", { required: true })}
              />
              <input
                type="hidden"
                {...register("status", { required: true })}
              />

              {/* Call Type Radio Buttons */}
              <div className="mb-4">
                <label className="block text-blue-700 font-bold mb-2">
                  Call Type <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4 flex-wrap">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      {...register("call_type", {
                        required: "Call type is required",
                      })}
                      value="video"
                      className="form-radio text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2">Video Call</span>
                  </label>

                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      {...register("call_type")}
                      value="voice"
                      className="form-radio text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2">Voice Call</span>
                  </label>

                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      {...register("call_type")}
                      value="walk-in"
                      className="form-radio text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2">Walk-In</span>
                  </label>
                </div>
                {errors.call_type && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.call_type.message}</span>
                )}
              </div>

              {/* Message to Candidate */}
              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">
                  Message to Candidate <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register("message", {
                    required: "Message is required",
                    minLength: {
                      value: 10,
                      message: "Message must be at least 10 characters long"
                    }
                  })}
                  rows={3}
                  className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical ${errors.message ? "border-red-500" : "border-gray-300"
                    }`}
                  placeholder="Enter your message to the candidate..."
                  disabled={isSubmitting}
                />
                {errors.message && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.message.message}</span>
                )}
              </div>

              {/* Hiring Team Email */}
              <div className="mb-4">
                <label className="block text-gray-700 font-bold mb-2">
                  Hiring Team Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register("hiring_team_email", {
                    required: "Email is required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.hiring_team_email ? "border-red-500" : "border-gray-300"
                    }`}
                  placeholder="example@company.com"
                  disabled={isSubmitting}
                />
                {errors.hiring_team_email && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.hiring_team_email.message}</span>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="w-full md:w-1/2 px-6 pt-6 pl-4">
              {/* Interview Date */}
              <div className="mb-4">
                <label className="block text-blue-700 font-bold mb-2">
                  Interview Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  {...register("interview_date", {
                    required: "Interview date is required",
                    validate: (value) => {
                      const selectedDate = new Date(value)
                      const today = new Date()
                      today.setHours(0, 0, 0, 0)
                      return selectedDate >= today || "Interview date cannot be in the past"
                    }
                  })}
                  min={new Date().toISOString().split('T')[0]}
                  className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.interview_date ? "border-red-500" : "border-gray-300"
                    }`}
                  disabled={isSubmitting}
                />
                {errors.interview_date && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.interview_date.message}</span>
                )}
              </div>

              {/* Starting Time */}
              <div className="mb-4">
                <label className="block text-blue-700 font-bold mb-2">
                  Starting Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  {...register("interview_time", {
                    required: "Start time is required",
                  })}
                  className={`block w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.interview_time ? "border-red-500" : "border-gray-300"
                    }`}
                  disabled={isSubmitting}
                />
                {errors.interview_time && (
                  <span className="text-red-500 text-sm mt-1 block">{errors.interview_time.message}</span>
                )}
              </div>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end px-3 pb-3 gap-2">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Scheduling...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faEnvelope} className="text-lg text-white mr-2" />
                  Send Interview Request
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(modalContent, document.body)
}

export default Modal